/**
 * Events Panel Component
 * Управление ивентами (только для super_admin)
 */

import React, { useState, useEffect } from "react";
import { Section, ToolbarButton } from "./UI";
import { useToast } from "./Toast";
import { adminService } from "../services/adminService";

export function EventsPanel({ setView }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [newEvent, setNewEvent] = useState({
    code: "",
    name: "",
    is_default: false,
  });
  const toast = useToast();

  // Автоматическая генерация кода из названия
  const generateCode = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Удаляем спецсимволы
      .replace(/\s+/g, '_') // Пробелы на подчеркивания
      .substring(0, 20); // Максимум 20 символов
  };

  // Фильтрация событий
  const filteredEvents = events.filter((event) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      event.code.toLowerCase().includes(query) ||
      event.name.toLowerCase().includes(query)
    );
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading events...');

      const events = await adminService.getEvents();
      console.log('Events loaded:', events);
      setEvents(events);
    } catch (err) {
      console.error("Failed to load events:", err);
      console.error('Error details:', err.message, err.stack);
      setError(err.message || "Ошибка загрузки ивентов");
      toast.error(`❗ ${err.message || 'Ошибка загрузки ивентов'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();

    if (!newEvent.name) {
      toast.error("Заполните название ивента");
      return;
    }

    // Генерируем код автоматически из названия
    const eventCode = generateCode(newEvent.name);

    try {
      await adminService.createEvent({
        code: eventCode,
        name: newEvent.name,
        is_default: newEvent.is_default
      });
      toast.success(`Ивент "${eventCode}" добавлен`);
      setNewEvent({ code: "", name: "", is_default: false });
      setShowAddForm(false);
      await loadEvents();
    } catch (err) {
      console.error("Failed to add event:", err);
      toast.error(`Ошибка добавления: ${err.message}`);
    }
  };

  const handleToggleDefault = async (eventId, currentIsDefault) => {
    try {
      await adminService.updateEvent(eventId, { is_default: !currentIsDefault });
      toast.success(currentIsDefault ? "Ивент больше не по умолчанию" : "Ивент установлен по умолчанию");
      await loadEvents();
    } catch (err) {
      console.error("Failed to update event:", err);
      toast.error(`Ошибка обновления: ${err.message}`);
    }
  };

  const handleToggleActive = async (eventId, currentIsActive) => {
    try {
      await adminService.updateEvent(eventId, { is_active: !currentIsActive });
      toast.success(currentIsActive ? "Ивент деактивирован" : "Ивент активирован");
      await loadEvents();
    } catch (err) {
      console.error("Failed to update event:", err);
      toast.error(`Ошибка обновления: ${err.message}`);
    }
  };

  const handleDeleteEvent = async (eventId, eventCode, leadsCount) => {
    if (leadsCount > 0) {
      toast.error(`Нельзя удалить ивент с ${leadsCount} лидами`);
      return;
    }

    if (!confirm(`Удалить ивент "${eventCode}"?`)) return;

    try {
      await adminService.deleteEvent(eventId);
      toast.success("Ивент удалён");
      await loadEvents();
    } catch (err) {
      console.error("Failed to delete event:", err);
      toast.error(`Ошибка удаления: ${err.message}`);
    }
  };

  if (loading && events.length === 0) {
    return (
      <Section title="🎉 Управление ивентами" right={<ToolbarButton onClick={() => setView("home")}>Назад</ToolbarButton>}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Загрузка ивентов...</p>
          </div>
        </div>
      </Section>
    );
  }

  if (error && events.length === 0) {
    return (
      <Section title="🎉 Управление ивентами" right={<ToolbarButton onClick={() => setView("home")}>Назад</ToolbarButton>}>
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">❌ {error}</p>
          <ToolbarButton onClick={loadEvents}>Попробовать снова</ToolbarButton>
        </div>
      </Section>
    );
  }

  return (
    <Section 
      title="🎉 Управление ивентами"
      right={
        <div className="flex gap-2">
          <ToolbarButton onClick={() => setShowAddForm(!showAddForm)} active={showAddForm}>
            {showAddForm ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              "+"
            )}
          </ToolbarButton>
          <ToolbarButton onClick={loadEvents}>🔄</ToolbarButton>
          <ToolbarButton onClick={() => setView("home")}>Назад</ToolbarButton>
        </div>
      }
    >
      {/* Поиск */}
      <div className="mb-4">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="🔍 Поиск по коду или названию..."
          className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 outline-none transition-colors"
        />
      </div>

      {/* Форма добавления ивента */}
      {showAddForm && (
        <form onSubmit={handleAddEvent} className="mb-6 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900">
          <h3 className="font-semibold mb-3">Добавить ивент</h3>
          <div>
            <label className="block text-sm font-medium mb-1">Название *</label>
            <input
              type="text"
              value={newEvent.name}
              onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
              placeholder="AWA Bangkok 25"
              className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 outline-none transition-colors"
              required
            />
          </div>
          <div className="flex items-center gap-3 mt-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newEvent.is_default}
                onChange={(e) => setNewEvent({ ...newEvent, is_default: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
              <span className="text-sm">По умолчанию</span>
            </label>
          </div>
          <div className="flex gap-2 mt-3">
            <ToolbarButton active type="submit">Добавить</ToolbarButton>
            <ToolbarButton type="button" onClick={() => setShowAddForm(false)}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </ToolbarButton>
          </div>
        </form>
      )}

      {/* Список ивентов */}
      <div className="space-y-2">
        {filteredEvents.map((event) => {
          const isDefault = event.display_order === 0;

          return (
            <div 
              key={event.id}
              className="flex flex-col p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex-1 min-w-0 mb-3">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <span className="font-medium truncate">{event.name}</span>
                  <span className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-xs font-mono shrink-0">{event.code}</span>
                  {isDefault && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 shrink-0">
                      По умолчанию
                    </span>
                  )}
                  {!event.is_active && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 shrink-0">
                      Неактивен
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Лидов: {event.leads_count}
                </div>
              </div>

              {/* Кнопки управления */}
              <div className="flex gap-1 shrink-0 flex-wrap pt-3 border-t border-gray-200 dark:border-gray-700">
                <ToolbarButton 
                  onClick={() => handleToggleDefault(event.id, isDefault)}
                  className="min-w-[36px] px-2"
                  title={isDefault ? "Убрать из умолчания" : "Сделать по умолчанию"}
                  active={isDefault}
                >
                  ⭐
                </ToolbarButton>
                <ToolbarButton 
                  onClick={() => handleToggleActive(event.id, event.is_active)}
                  className="min-w-[36px] px-2"
                  title={event.is_active ? "Деактивировать" : "Активировать"}
                >
                  {event.is_active ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </ToolbarButton>
                <ToolbarButton 
                  onClick={() => handleDeleteEvent(event.id, event.code, event.leads_count)}
                  className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 min-w-[36px] px-2"
                  title="Удалить ивент"
                  disabled={event.leads_count > 0}
                >
                  🗑️
                </ToolbarButton>
              </div>
            </div>
          );
        })}
      </div>

      {filteredEvents.length === 0 && !loading && (
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          {searchQuery ? "Ничего не найдено" : "Нет ивентов"}
        </p>
      )}
    </Section>
  );
}
